import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ToggleLeft, ToggleRight } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { type FeatureFlag, type TenantRef, apiError } from '../lib';

/**
 * A global switch with per-tenant exceptions. An unknown flag resolves to
 * `false`, so a client shipping ahead of the flag degrades to off, not to a 404.
 */
export default function FeatureFlags() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canManage = can('platform.manage_flags');

  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [tenants, setTenants] = useState<TenantRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FeatureFlag | null>(null);
  const [form, setForm] = useState({ key: '', description: '', enabled: false });
  const [saving, setSaving] = useState(false);

  const [overrideFor, setOverrideFor] = useState<FeatureFlag | null>(null);
  const [overrideTenantId, setOverrideTenantId] = useState('');
  const [overrideEnabled, setOverrideEnabled] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<{ data: FeatureFlag[] }>('/platform/flags', {
        params: { limit: 100, search: search || undefined },
      })
      .then(({ data }) => setFlags(data.data ?? []))
      .catch((e) => setError(apiError(e, t('platform.flags.loadFailed'))))
      .finally(() => setLoading(false));
  }, [search, t]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    api
      .get<{ data: TenantRef[] }>('/tenants', { params: { limit: 200 } })
      .then(({ data }) => setTenants(data.data ?? []))
      .catch(() => setTenants([]));
  }, []);

  const tenantName = (id: string) => tenants.find((tenant) => tenant.id === id)?.name ?? id;

  const openCreate = () => {
    setEditing(null);
    setForm({ key: '', description: '', enabled: false });
    setFormOpen(true);
  };

  const openEdit = (flag: FeatureFlag) => {
    setEditing(flag);
    setForm({ key: flag.key, description: flag.description ?? '', enabled: flag.enabled });
    setFormOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (editing) {
        // The key is the flag's identity — clients hardcode it, so it is immutable.
        await api.patch(`/platform/flags/${editing.key}`, {
          description: form.description || undefined,
          enabled: form.enabled,
        });
      } else {
        await api.post('/platform/flags', {
          key: form.key,
          description: form.description || undefined,
          enabled: form.enabled,
        });
      }
      toast.success(t(editing ? 'platform.flags.updated' : 'platform.flags.created'));
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e, t('platform.flags.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const toggleDefault = async (flag: FeatureFlag) => {
    try {
      await api.patch(`/platform/flags/${flag.key}`, { enabled: !flag.enabled });
      load();
    } catch (e) {
      toast.error(apiError(e, t('platform.flags.saveFailed')));
    }
  };

  const removeFlag = async (flag: FeatureFlag) => {
    if (!window.confirm(t('platform.flags.deleteConfirm', { key: flag.key }))) return;
    try {
      await api.delete(`/platform/flags/${flag.key}`);
      toast.success(t('platform.flags.deleted'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('platform.flags.saveFailed')));
    }
  };

  const saveOverride = async () => {
    if (!overrideFor || !overrideTenantId) return;
    try {
      await api.put(`/platform/flags/${overrideFor.key}/overrides/${overrideTenantId}`, {
        enabled: overrideEnabled,
      });
      toast.success(t('platform.flags.overrideSet'));
      setOverrideFor(null);
      setOverrideTenantId('');
      load();
    } catch (e) {
      toast.error(apiError(e, t('platform.flags.saveFailed')));
    }
  };

  const clearOverride = async (flag: FeatureFlag, tenantId: string) => {
    try {
      await api.delete(`/platform/flags/${flag.key}/overrides/${tenantId}`);
      toast.success(t('platform.flags.overrideCleared'));
      load();
    } catch (e) {
      toast.error(apiError(e, t('platform.flags.saveFailed')));
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">{t('nav.featureFlags')}</h1>
          <p className="text-sm text-[var(--muted-foreground)]">{t('platform.flags.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            className="w-full sm:w-56"
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {canManage && <Button onClick={openCreate}>{t('platform.flags.newFlag')}</Button>}
        </div>
      </div>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : flags.length === 0 ? (
        <EmptyState title={t('platform.flags.empty')} hint={t('platform.flags.emptyHint')} />
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          {flags.map((flag) => {
            const overrides = Object.entries(flag.overrides ?? {});
            return (
              <Card key={flag.id}>
                <CardHeader className="flex-row items-start justify-between gap-2">
                  <div className="min-w-0">
                    <CardTitle className="truncate">
                      <code>{flag.key}</code>
                    </CardTitle>
                    <p className="text-xs text-[var(--muted-foreground)]">
                      {flag.description ?? t('platform.flags.noDescription')}
                    </p>
                  </div>
                  <button
                    onClick={() => canManage && toggleDefault(flag)}
                    disabled={!canManage}
                    className="flex shrink-0 items-center gap-1.5 text-sm"
                    aria-label={t('platform.flags.toggleDefault')}
                  >
                    {flag.enabled ? (
                      <ToggleRight className="h-6 w-6 text-green-600" />
                    ) : (
                      <ToggleLeft className="h-6 w-6 text-[var(--muted-foreground)]" />
                    )}
                    <Badge tone={flag.enabled ? 'success' : 'neutral'}>
                      {t(flag.enabled ? 'platform.flags.onByDefault' : 'platform.flags.offByDefault')}
                    </Badge>
                  </button>
                </CardHeader>

                <CardContent className="space-y-3">
                  <div>
                    <h4 className="text-sm font-medium">{t('platform.flags.overrides')}</h4>
                    {overrides.length === 0 ? (
                      <p className="text-xs text-[var(--muted-foreground)]">
                        {t('platform.flags.noOverrides')}
                      </p>
                    ) : (
                      <ul className="mt-2 divide-y rounded-lg border text-sm">
                        {overrides.map(([tenantId, enabled]) => (
                          <li
                            key={tenantId}
                            className="flex items-center justify-between gap-2 p-2.5"
                          >
                            <span className="min-w-0 truncate">{tenantName(tenantId)}</span>
                            <div className="flex items-center gap-2">
                              <Badge tone={enabled ? 'success' : 'danger'}>
                                {t(enabled ? 'platform.flags.forcedOn' : 'platform.flags.forcedOff')}
                              </Badge>
                              {canManage && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => clearOverride(flag, tenantId)}
                                >
                                  {t('platform.flags.clear')}
                                </Button>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {canManage && (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setOverrideFor(flag);
                          setOverrideTenantId('');
                          setOverrideEnabled(!flag.enabled);
                        }}
                      >
                        {t('platform.flags.addOverride')}
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => openEdit(flag)}>
                        {t('common.edit')}
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => removeFlag(flag)}>
                        {t('common.delete')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={t(editing ? 'platform.flags.editFlag' : 'platform.flags.newFlag')}
        description={t('platform.flags.formHint')}
        footer={
          <>
            <Button variant="outline" onClick={() => setFormOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={submit} disabled={saving || (!editing && !form.key.trim())}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field
            label={t('platform.flags.key')}
            required={!editing}
            hint={editing ? t('platform.flags.keyImmutable') : t('platform.flags.keyHint')}
          >
            <Input
              disabled={!!editing}
              value={form.key}
              onChange={(e) => setForm({ ...form, key: e.target.value })}
            />
          </Field>
          <Field label={t('platform.flags.description')}>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
            {t('platform.flags.onByDefault')}
          </label>
        </div>
      </Dialog>

      {overrideFor && (
        <Dialog
          open
          onClose={() => setOverrideFor(null)}
          title={t('platform.flags.overrideTitle', { key: overrideFor.key })}
          description={t('platform.flags.overrideHint')}
          footer={
            <>
              <Button variant="outline" onClick={() => setOverrideFor(null)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={saveOverride} disabled={!overrideTenantId}>
                {t('common.save')}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <Field label={t('platform.subscriptions.tenant')} required>
              <Select
                value={overrideTenantId}
                onChange={(e) => setOverrideTenantId(e.target.value)}
              >
                <option value="">{t('platform.subscriptions.chooseTenant')}</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('platform.flags.valueForTenant')}>
              <Select
                value={overrideEnabled ? 'on' : 'off'}
                onChange={(e) => setOverrideEnabled(e.target.value === 'on')}
              >
                <option value="on">{t('platform.flags.forcedOn')}</option>
                <option value="off">{t('platform.flags.forcedOff')}</option>
              </Select>
            </Field>
          </div>
        </Dialog>
      )}
    </div>
  );
}
